import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import {
  Award,
  Plus,
  Trash2,
  Pencil,
  Star,
  Upload,
  Loader2,
  X,
  User,
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
  Heart,
  ThumbsUp,
  Quote,
  Eye,
  Settings,
  ExternalLink,
  QrCode,
  Send
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import LikesManagement from './LikesManagement';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CommunityManagement = ({ language = 'fr' }) => {
  const [activeTab, setActiveTab] = useState('agent');
  
  // Agent Honor State
  const [agents, setAgents] = useState([]);
  const [pendingComments, setPendingComments] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  
  // Testimonials State
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialFilter, setTestimonialFilter] = useState('pending');
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);
  
  // Google Reviews Config State
  const [googleConfig, setGoogleConfig] = useState(null);
  const [googleReviewUrl, setGoogleReviewUrl] = useState('');
  const [pharmacyName, setPharmacyName] = useState('Pharmacie Saint Ange Des Amis');
  const [reviewStats, setReviewStats] = useState(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Agent Dialog
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [agentName, setAgentName] = useState('');
  const [agentRole, setAgentRole] = useState('');
  const [agentDescription, setAgentDescription] = useState('');
  const [agentPeriodType, setAgentPeriodType] = useState('month');
  const [agentPeriodLabel, setAgentPeriodLabel] = useState('');
  const [agentPhotoPreview, setAgentPhotoPreview] = useState(null);
  const [agentPhotoBase64, setAgentPhotoBase64] = useState('');
  const [saving, setSaving] = useState(false);
  
  const fileInputRef = useRef(null);

  // Fetch data
  const fetchAgents = useCallback(async () => {
    setLoadingAgents(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/community/agent-honor/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgents(response.data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  const fetchPendingComments = useCallback(async () => {
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/community/agent-honor/comments/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingComments(response.data || []);
    } catch (error) {
      console.error('Error fetching pending comments:', error);
    }
  }, []);

  const fetchTestimonials = useCallback(async () => {
    setLoadingTestimonials(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const params = testimonialFilter !== 'all' ? `?status=${testimonialFilter}` : '';
      const response = await axios.get(`${API_URL}/community/testimonials/all${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestimonials(response.data || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoadingTestimonials(false);
    }
  }, [testimonialFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/community/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
    fetchPendingComments();
    fetchStats();
    fetchGoogleConfig();
    fetchReviewStats();
  }, [fetchAgents, fetchPendingComments, fetchStats]);

  // Fetch Google Reviews config
  const fetchGoogleConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/reviews/config`);
      setGoogleConfig(response.data);
      setGoogleReviewUrl(response.data.google_review_url || '');
      setPharmacyName(response.data.pharmacy_name || 'Pharmacie Saint Ange Des Amis');
    } catch (error) {
      console.error('Error fetching google config:', error);
    }
  };

  // Fetch Review request stats
  const fetchReviewStats = async () => {
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/reviews/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviewStats(response.data);
    } catch (error) {
      console.error('Error fetching review stats:', error);
    }
  };

  // Save Google Config
  const handleSaveGoogleConfig = async () => {
    setSavingConfig(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.put(
        `${API_URL}/reviews/config`,
        {
          google_review_url: googleReviewUrl,
          pharmacy_name: pharmacyName
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Configuration Google sauvegardée');
      setConfigDialogOpen(false);
      fetchGoogleConfig();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingConfig(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'testimonials') {
      fetchTestimonials();
    }
  }, [activeTab, fetchTestimonials]);

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 5MB)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setAgentPhotoPreview(event.target.result);
      setAgentPhotoBase64(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Agent CRUD
  const openNewAgentDialog = () => {
    setEditingAgent(null);
    setAgentName('');
    setAgentRole('');
    setAgentDescription('');
    setAgentPeriodType('month');
    setAgentPeriodLabel('');
    setAgentPhotoPreview(null);
    setAgentPhotoBase64('');
    setAgentDialogOpen(true);
  };

  const openEditAgentDialog = (agent) => {
    setEditingAgent(agent);
    setAgentName(agent.name);
    setAgentRole(agent.role);
    setAgentDescription(agent.description);
    setAgentPeriodType(agent.period_type);
    setAgentPeriodLabel(agent.period_label || '');
    setAgentPhotoPreview(agent.photo_url);
    setAgentPhotoBase64('');
    setAgentDialogOpen(true);
  };

  const handleSaveAgent = async () => {
    if (!agentName.trim() || !agentRole.trim() || !agentDescription.trim()) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const payload = {
        name: agentName,
        role: agentRole,
        description: agentDescription,
        period_type: agentPeriodType,
        period_label: agentPeriodLabel || null
      };

      if (agentPhotoBase64) {
        payload.photo_base64 = agentPhotoBase64;
      }

      if (editingAgent) {
        await axios.put(
          `${API_URL}/community/agent-honor/${editingAgent.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Agent mis à jour');
      } else {
        await axios.post(
          `${API_URL}/community/agent-honor`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Agent créé et mis en avant');
      }

      setAgentDialogOpen(false);
      fetchAgents();
    } catch (error) {
      console.error('Error saving agent:', error);
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAgent = async (agent) => {
    if (!confirm(`Supprimer "${agent.name}" ?`)) return;

    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.delete(`${API_URL}/community/agent-honor/${agent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Agent supprimé');
      fetchAgents();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleActivateAgent = async (agent) => {
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.put(
        `${API_URL}/community/agent-honor/${agent.id}`,
        { is_active: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${agent.name} est maintenant à l'honneur`);
      fetchAgents();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Comment moderation
  const handleModerateComment = async (commentId, status) => {
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.put(
        `${API_URL}/community/agent-honor/comments/${commentId}/moderate`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(status === 'approved' ? 'Commentaire approuvé' : 'Commentaire rejeté');
      fetchPendingComments();
      fetchStats();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Testimonial moderation
  const handleModerateTestimonial = async (testimonialId, status, isFeatured = null) => {
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.put(
        `${API_URL}/community/testimonials/${testimonialId}/moderate`,
        { status, is_featured: isFeatured },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(status === 'approved' ? 'Avis approuvé' : 'Avis rejeté');
      fetchTestimonials();
      fetchStats();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleToggleFeatured = async (testimonial) => {
    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.put(
        `${API_URL}/community/testimonials/${testimonial.id}/feature`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(testimonial.is_featured ? 'Retiré de la une' : 'Mis en vedette');
      fetchTestimonials();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteTestimonial = async (testimonial) => {
    if (!confirm('Supprimer cet avis ?')) return;

    try {
      const token = localStorage.getItem('dermascan_token');
      await axios.delete(`${API_URL}/community/testimonials/${testimonial.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Avis supprimé');
      fetchTestimonials();
      fetchStats();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-amber-600" />
                <div>
                  <p className="text-2xl font-bold text-amber-700">{stats.pending_comments}</p>
                  <p className="text-xs text-amber-600">Commentaires en attente</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold text-blue-700">{stats.pending_testimonials}</p>
                  <p className="text-xs text-blue-600">Avis en attente</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-2xl font-bold text-green-700">{stats.approved_testimonials}</p>
                  <p className="text-xs text-green-600">Avis publiés</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Star className="w-8 h-8 text-purple-600" />
                <div>
                  <p className="text-2xl font-bold text-purple-700">{stats.featured_testimonials}</p>
                  <p className="text-xs text-purple-600">Avis en vedette</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="agent" className="flex items-center gap-2">
            <Award className="w-4 h-4" />
            Agent du Mois
            {stats?.pending_comments > 0 && (
              <Badge className="bg-red-500 text-white text-xs">{stats.pending_comments}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="testimonials" className="flex items-center gap-2">
            <Quote className="w-4 h-4" />
            Avis Clients
            {stats?.pending_testimonials > 0 && (
              <Badge className="bg-red-500 text-white text-xs">{stats.pending_testimonials}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="google" className="flex items-center gap-2">
            <Star className="w-4 h-4" />
            Google Avis
          </TabsTrigger>
          <TabsTrigger value="likes" className="flex items-center gap-2" data-testid="likes-tab">
            <Heart className="w-4 h-4 text-red-500" />
            Likes
          </TabsTrigger>
        </TabsList>

        {/* Likes Management Tab */}
        <TabsContent value="likes" className="space-y-4">
          <LikesManagement language={language} />
        </TabsContent>

        {/* Google Reviews Tab */}
        <TabsContent value="google" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  Configuration Google Business
                </CardTitle>
                <Button onClick={() => setConfigDialogOpen(true)} variant="outline">
                  <Settings className="w-4 h-4 mr-2" />
                  Configurer
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Config */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-medium text-gray-700 mb-3">Lien actuel</h4>
                {googleConfig?.google_review_url ? (
                  <div className="flex items-center gap-2">
                    <code className="bg-white px-3 py-2 rounded border text-sm flex-1 truncate">
                      {googleConfig.google_review_url}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(googleConfig.google_review_url, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <p className="text-amber-600 text-sm">
                    ⚠️ Aucun lien configuré. Les visiteurs seront redirigés vers une recherche Google Maps.
                  </p>
                )}
              </div>

              {/* QR Code Preview */}
              {googleConfig?.google_review_url && (
                <div className="bg-white border rounded-xl p-6 text-center">
                  <h4 className="font-medium text-gray-700 mb-4">QR Code à imprimer</h4>
                  <div className="inline-block bg-white p-4 rounded-xl shadow-inner border">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(googleConfig.google_review_url)}&bgcolor=ffffff&color=2E7D32`}
                      alt="QR Code Google Review"
                      className="w-48 h-48"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-3">
                    Imprimez ce QR code et placez-le au comptoir
                  </p>
                  <Button
                    variant="outline"
                    className="mt-3"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(googleConfig.google_review_url)}&bgcolor=ffffff&color=2E7D32&format=png`;
                      link.download = 'qr-code-avis-google.png';
                      link.click();
                    }}
                  >
                    Télécharger le QR Code
                  </Button>
                </div>
              )}

              {/* WhatsApp Stats */}
              {reviewStats && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <Send className="w-8 h-8 text-green-600" />
                      <div>
                        <p className="text-2xl font-bold text-green-700">{reviewStats.total_requests}</p>
                        <p className="text-xs text-green-600">Demandes WhatsApp envoyées</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <Clock className="w-8 h-8 text-blue-600" />
                      <div>
                        <p className="text-2xl font-bold text-blue-700">{reviewStats.last_7_days}</p>
                        <p className="text-xs text-blue-600">Ces 7 derniers jours</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h4 className="font-medium text-blue-800 mb-2">💡 Comment obtenir le lien Google ?</h4>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal ml-4">
                  <li>Allez sur <a href="https://business.google.com" target="_blank" rel="noopener" className="underline">Google Business Profile</a></li>
                  <li>Connectez-vous avec le compte de la pharmacie</li>
                  <li>Cliquez sur "Obtenir plus d'avis"</li>
                  <li>Copiez le lien court fourni</li>
                  <li>Collez-le dans la configuration ci-dessus</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Agent Tab */}
        <TabsContent value="agent" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-600" />
                  Gestion de l'Agent à l'Honneur
                </CardTitle>
                <Button onClick={openNewAgentDialog} className="bg-amber-500 hover:bg-amber-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvel agent
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingAgents ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                </div>
              ) : agents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Award className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p>Aucun agent à l'honneur</p>
                  <Button variant="outline" className="mt-3" onClick={openNewAgentDialog}>
                    Créer le premier
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {agents.map((agent) => (
                    <div
                      key={agent.id}
                      className={`flex items-center gap-4 p-4 rounded-lg border ${
                        agent.is_active ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {agent.photo_url ? (
                        <img
                          src={agent.photo_url}
                          alt={agent.name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold">{agent.name}</h4>
                          {agent.is_active && (
                            <Badge className="bg-amber-500 text-white">Actif</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{agent.role}</p>
                        <p className="text-xs text-gray-500">{agent.period_label}</p>
                      </div>
                      <div className="flex gap-2">
                        {!agent.is_active && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleActivateAgent(agent)}
                            className="text-amber-600 border-amber-300"
                          >
                            <Star className="w-4 h-4 mr-1" />
                            Activer
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditAgentDialog(agent)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600"
                          onClick={() => handleDeleteAgent(agent)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending Comments */}
          {pendingComments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <Clock className="w-5 h-5" />
                  Commentaires en attente ({pendingComments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingComments.map((comment) => (
                    <div
                      key={comment.id}
                      className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-sm">{comment.author_name}</p>
                        <p className="text-gray-600 text-sm mt-1">{comment.content}</p>
                        <p className="text-xs text-gray-400 mt-1">{formatDate(comment.created_at)}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-300"
                          onClick={() => handleModerateComment(comment.id, 'approved')}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-300"
                          onClick={() => handleModerateComment(comment.id, 'rejected')}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Testimonials Tab */}
        <TabsContent value="testimonials" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Quote className="w-5 h-5 text-blue-600" />
                  Gestion des Avis Clients
                </CardTitle>
                <Select value={testimonialFilter} onValueChange={setTestimonialFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="approved">Approuvés</SelectItem>
                    <SelectItem value="rejected">Rejetés</SelectItem>
                    <SelectItem value="all">Tous</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {loadingTestimonials ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : testimonials.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p>Aucun avis {testimonialFilter === 'pending' ? 'en attente' : ''}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {testimonials.map((testimonial) => (
                    <div
                      key={testimonial.id}
                      className={`p-4 rounded-lg border ${
                        testimonial.status === 'pending' ? 'bg-amber-50 border-amber-200' :
                        testimonial.status === 'approved' ? 'bg-green-50 border-green-200' :
                        'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium">{testimonial.author_name}</span>
                            <Badge
                              variant="outline"
                              className={
                                testimonial.status === 'pending' ? 'text-amber-700 border-amber-300' :
                                testimonial.status === 'approved' ? 'text-green-700 border-green-300' :
                                'text-red-700 border-red-300'
                              }
                            >
                              {testimonial.status === 'pending' ? 'En attente' :
                               testimonial.status === 'approved' ? 'Approuvé' : 'Rejeté'}
                            </Badge>
                            {testimonial.is_featured && (
                              <Badge className="bg-amber-500 text-white">
                                <Star className="w-3 h-3 mr-1" />
                                Vedette
                              </Badge>
                            )}
                          </div>
                          <p className="text-gray-600">{testimonial.content}</p>
                          <p className="text-xs text-gray-400 mt-2">{formatDate(testimonial.created_at)}</p>
                        </div>
                        <div className="flex flex-col gap-2">
                          {testimonial.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 border-green-300"
                                onClick={() => handleModerateTestimonial(testimonial.id, 'approved')}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approuver
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-300"
                                onClick={() => handleModerateTestimonial(testimonial.id, 'rejected')}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Rejeter
                              </Button>
                            </>
                          )}
                          {testimonial.status === 'approved' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className={testimonial.is_featured ? 'text-amber-600' : 'text-gray-600'}
                              onClick={() => handleToggleFeatured(testimonial)}
                            >
                              <Star className={`w-4 h-4 mr-1 ${testimonial.is_featured ? 'fill-amber-500' : ''}`} />
                              {testimonial.is_featured ? 'Retirer' : 'Vedette'}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                            onClick={() => handleDeleteTestimonial(testimonial)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Agent Dialog */}
      <Dialog open={agentDialogOpen} onOpenChange={setAgentDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-600" />
              {editingAgent ? 'Modifier l\'agent' : 'Nouvel agent à l\'honneur'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Photo */}
            <div>
              <Label>Photo</Label>
              <div
                className="mt-2 border-2 border-dashed border-gray-300 rounded-xl p-4 text-center cursor-pointer hover:border-amber-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {agentPhotoPreview ? (
                  <div className="relative inline-block">
                    <img
                      src={agentPhotoPreview}
                      alt="Preview"
                      className="w-24 h-24 rounded-full object-cover mx-auto"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setAgentPhotoPreview(null);
                        setAgentPhotoBase64('');
                      }}
                      className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="py-4">
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">Cliquez pour ajouter une photo</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="agent-name">Nom *</Label>
              <Input
                id="agent-name"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                placeholder="Ex: Marie Dupont"
              />
            </div>

            {/* Role */}
            <div>
              <Label htmlFor="agent-role">Fonction *</Label>
              <Input
                id="agent-role"
                value={agentRole}
                onChange={(e) => setAgentRole(e.target.value)}
                placeholder="Ex: Conseillère en dermo-cosmétique"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="agent-desc">Présentation *</Label>
              <Textarea
                id="agent-desc"
                value={agentDescription}
                onChange={(e) => setAgentDescription(e.target.value)}
                placeholder="Décrivez les qualités et contributions de cet agent..."
                rows={3}
              />
            </div>

            {/* Period */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type de période</Label>
                <Select value={agentPeriodType} onValueChange={setAgentPeriodType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Mois</SelectItem>
                    <SelectItem value="quarter">Trimestre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="period-label">Libellé (optionnel)</Label>
                <Input
                  id="period-label"
                  value={agentPeriodLabel}
                  onChange={(e) => setAgentPeriodLabel(e.target.value)}
                  placeholder="Ex: Avril 2026"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAgentDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSaveAgent}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingAgent ? 'Enregistrer' : 'Créer et activer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Google Config Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuration Google Business
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="pharmacy-name">Nom de la pharmacie</Label>
              <Input
                id="pharmacy-name"
                value={pharmacyName}
                onChange={(e) => setPharmacyName(e.target.value)}
                placeholder="Pharmacie Saint Ange Des Amis"
              />
            </div>
            <div>
              <Label htmlFor="google-url">Lien Google Review</Label>
              <Input
                id="google-url"
                value={googleReviewUrl}
                onChange={(e) => setGoogleReviewUrl(e.target.value)}
                placeholder="https://g.page/r/..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Le lien court fourni par Google Business Profile
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSaveGoogleConfig}
              disabled={savingConfig}
              className="bg-[#2E7D32] hover:bg-[#1B5E20]"
            >
              {savingConfig && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunityManagement;
